import { useSyncExternalStore } from "react";
import { isPrintProduct } from "../prints/printProducts";

// The cart holds ids and quantities only. Names and prices are always read fresh
// from /products, and 450labs prices the order itself. It survives a reload and
// stays in step across tabs, so the header's "(n)" is right everywhere.

export interface CartLine {
  productId: string;
  variantId?: string;
  quantity: number;
}

const STORAGE_KEY = "n450s.marketplace.cart.v1";
const MAX_QUANTITY = 20;

function read(): CartLine[] {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (l) => l && typeof l.productId === "string" && Number.isInteger(l.quantity) && l.quantity > 0,
    );
  } catch {
    return [];
  }
}

let lines: CartLine[] = typeof window === "undefined" ? [] : read();
const listeners = new Set<() => void>();

function commit(next: CartLine[]) {
  lines = next;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // private mode or full storage: the cart still works for this page view
  }
  listeners.forEach((l) => l());
}

const sameLine = (a: CartLine, productId: string, variantId?: string) =>
  a.productId === productId && (a.variantId ?? "") === (variantId ?? "");

export const cart = {
  add(productId: string, variantId?: string, quantity = 1) {
    // AOTD prints are pre-order only: they never go in the cart
    if (isPrintProduct(productId)) return;
    const existing = lines.find((l) => sameLine(l, productId, variantId));
    if (existing) {
      cart.setQuantity(productId, variantId, existing.quantity + quantity);
      return;
    }
    commit([...lines, { productId, variantId, quantity: Math.min(quantity, MAX_QUANTITY) }]);
  },
  setQuantity(productId: string, variantId: string | undefined, quantity: number) {
    if (quantity <= 0) return cart.remove(productId, variantId);
    commit(
      lines.map((l) =>
        sameLine(l, productId, variantId) ? { ...l, quantity: Math.min(quantity, MAX_QUANTITY) } : l,
      ),
    );
  },
  remove(productId: string, variantId?: string) {
    commit(lines.filter((l) => !sameLine(l, productId, variantId)));
  },
  clear() {
    commit([]);
  },
};

function subscribe(listener: () => void) {
  listeners.add(listener);
  const onStorage = (e: StorageEvent) => {
    if (e.key !== STORAGE_KEY) return;
    lines = read();
    listener();
  };
  window.addEventListener("storage", onStorage);
  return () => {
    listeners.delete(listener);
    window.removeEventListener("storage", onStorage);
  };
}

const getSnapshot = () => lines;
const getServerSnapshot = (): CartLine[] => [];

export function useCart(): CartLine[] {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

export function useCartCount(): number {
  return useCart().reduce((sum, l) => sum + l.quantity, 0);
}
